import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../../styles/CookieBanner.css";

/**
 * Cookie consent banner (UK GDPR / PECR).
 *
 * Replaces the react-cookie-consent binary banner. Requirements it meets:
 * - "Accept all" and "Reject all" have EQUAL prominence on the first layer
 *   (identical styling) — ICO's headline requirement.
 * - "Manage choices" exposes per-category toggles (essential always on).
 * - Reopenable at any time: the footer's "Cookie settings" link opens it in
 *   settings mode even after a choice was saved.
 * - No non-essential storage happens before a choice (tags are gated on the
 *   consent state this banner writes).
 */
const CookieBanner = ({
  consent,
  consentDecided,
  settingsOpen,
  acceptAll,
  rejectAll,
  saveChoices,
  closeSettings,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(consent);

  // Keep the draft in sync when the panel (re)opens with saved choices.
  useEffect(() => {
    if (settingsOpen || expanded) setDraft(consent);
    if (settingsOpen) setExpanded(true);
  }, [settingsOpen, expanded, consent]);

  const visible = !consentDecided || settingsOpen;
  if (!visible) return null;

  return (
    <div
      className="cookie-banner"
      role="region"
      aria-label="Cookie choices"
      data-testid="cookie-banner"
    >
      <div className="cookie-banner-inner">
        <p className="cookie-banner-text">
          We use essential cookies to make the site work (like keeping you
          logged in). With your permission we'd also like to use{" "}
          <strong>analytics</strong> cookies to understand how the site is used
          and <strong>advertising</strong> cookies to measure ads. You can
          change your mind any time via "Cookie settings" in the footer. See
          our <Link to="/policies">Privacy Policy</Link>.
        </p>

        {expanded && (
          <div className="cookie-banner-choices">
            <div className="cookie-choice">
              <div className="cookie-choice-info">
                <span className="cookie-choice-name">Essential</span>
                <span className="cookie-choice-desc">
                  Login, security and core features. Always on.
                </span>
              </div>
              <input type="checkbox" checked disabled aria-label="Essential cookies (always on)" />
            </div>
            <div className="cookie-choice">
              <div className="cookie-choice-info">
                <span className="cookie-choice-name">Analytics</span>
                <span className="cookie-choice-desc">
                  Anonymous usage stats (Google Analytics, Microsoft Clarity)
                  that help us improve the site.
                </span>
              </div>
              <input
                type="checkbox"
                checked={draft.analytics}
                onChange={(e) => setDraft({ ...draft, analytics: e.target.checked })}
                aria-label="Allow analytics cookies"
              />
            </div>
            <div className="cookie-choice">
              <div className="cookie-choice-info">
                <span className="cookie-choice-name">Advertising</span>
                <span className="cookie-choice-desc">
                  Ad measurement and personalisation (Google, Meta). Without
                  this, ads still show but aren't personalised.
                </span>
              </div>
              <input
                type="checkbox"
                checked={draft.marketing}
                onChange={(e) => setDraft({ ...draft, marketing: e.target.checked })}
                aria-label="Allow advertising cookies"
              />
            </div>
          </div>
        )}

        <div className="cookie-banner-actions">
          {/* Reject and Accept share one class: equal prominence is a legal
              requirement, not a styling choice. */}
          <button type="button" className="cookie-btn cookie-btn-main" onClick={rejectAll}>
            Reject all
          </button>
          <button type="button" className="cookie-btn cookie-btn-main" onClick={acceptAll}>
            Accept all
          </button>
          {expanded ? (
            <button
              type="button"
              className="cookie-btn cookie-btn-ghost"
              onClick={() => saveChoices(draft)}
            >
              Save choices
            </button>
          ) : (
            <button
              type="button"
              className="cookie-btn cookie-btn-ghost"
              onClick={() => setExpanded(true)}
            >
              Manage choices
            </button>
          )}
          {settingsOpen && (
            <button
              type="button"
              className="cookie-btn cookie-btn-ghost"
              onClick={() => {
                setExpanded(false);
                closeSettings();
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
